import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "motion/react";
import { ChevronLeft } from "lucide-react";
import { lookupStudentForParent } from "../lib/api";

const BRAND_NAVY = "#161b3d";
const LANG_STORAGE_KEY = "forumLang";
const SESSION_KEY = "parentSession";

function useLang(): "es" | "en" {
  try {
    const s = localStorage.getItem(LANG_STORAGE_KEY);
    return s === "en" ? "en" : "es";
  } catch (_) {
    return "es";
  }
}

export function ParentLogin() {
  const navigate = useNavigate();
  const lang = useLang();

  const [cardCode, setCardCode] = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const T = {
    back: lang === "es" ? "Volver" : "Back",
    title: lang === "es" ? "Portal de Padres" : "Parent Portal",
    subtitle:
      lang === "es"
        ? "Ingresa el código del estudiante y tu correo para ver tus inscripciones."
        : "Enter the student code and your email to view your enrollments.",
    codeLabel: lang === "es" ? "Código del estudiante" : "Student code",
    codePlaceholder: "E002541",
    emailLabel: lang === "es" ? "Correo del padre / tutor" : "Parent / guardian email",
    emailPlaceholder: lang === "es" ? "correo@ejemplo.com" : "email@example.com",
    emptyError:
      lang === "es"
        ? "Ingresa el código del estudiante y tu correo."
        : "Enter the student code and your email.",
    notFound:
      lang === "es"
        ? "No encontramos una inscripción con esos datos."
        : "We couldn't find an enrollment with those details.",
    genericError:
      lang === "es" ? "Ocurrió un error. Intenta de nuevo." : "An error occurred. Please try again.",
    checking: lang === "es" ? "Verificando…" : "Verifying…",
    enter: lang === "es" ? "Acceder" : "Access",
    registerCta: lang === "es" ? "Inscríbete aquí" : "Register here",
    noAccountQuestion:
      lang === "es" ? "¿Aún no te has inscrito?" : "Not enrolled yet?",
    footer:
      lang === "es"
        ? "American School · Escuela de Talentos"
        : "American School · School of Talents",
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!cardCode.trim() || !parentEmail.trim()) {
      setError(T.emptyError);
      return;
    }
    setLoading(true);
    try {
      const result = await lookupStudentForParent(cardCode.trim(), parentEmail.trim());
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({ cardCode: result.cardCode, parentEmail: parentEmail.trim().toLowerCase() }));
      navigate("/parent/dashboard", { replace: true });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "";
      if (msg.includes("not-found") || msg.includes("No encontramos")) {
        setError(T.notFound);
      } else {
        setError(T.genericError);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="relative min-h-screen flex items-center justify-center bg-[#f5f5f7] p-4 font-sans"
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
    >
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="absolute top-4 left-4 flex items-center gap-1 text-[13px] font-medium text-[#6e6e73] hover:text-[#161b3d] transition-colors"
      >
        <ChevronLeft size={16} />
        {T.back}
      </button>

      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-8">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center overflow-hidden"
            style={{ backgroundColor: BRAND_NAVY }}
          >
            <img
              src="/logo/logo.png"
              alt="American School"
              className="w-full h-full object-contain"
            />
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-sm border border-black/[0.06] px-8 py-8">
          <h1
            className="text-[22px] font-bold tracking-tight text-center mb-1"
            style={{ color: BRAND_NAVY }}
          >
            {T.title}
          </h1>
          <p className="text-[13px] text-[#6e6e73] text-center mb-6">
            {T.subtitle}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[12px] font-semibold text-[#1d1d1f] uppercase tracking-wide mb-1.5">
                {T.codeLabel}
              </label>
              <input
                type="text"
                autoComplete="off"
                autoCapitalize="characters"
                value={cardCode}
                onChange={(e) => setCardCode(e.target.value)}
                placeholder={T.codePlaceholder}
                className="w-full px-3.5 py-2.5 rounded-xl border border-[#d2d2d7]/80 focus:border-[#161b3d] focus:ring-2 focus:ring-[#161b3d]/20 outline-none text-[14px] text-[#1d1d1f] bg-[#f5f5f7] focus:bg-white transition-all font-mono"
              />
            </div>

            <div>
              <label className="block text-[12px] font-semibold text-[#1d1d1f] uppercase tracking-wide mb-1.5">
                {T.emailLabel}
              </label>
              <input
                type="email"
                autoComplete="email"
                value={parentEmail}
                onChange={(e) => setParentEmail(e.target.value)}
                placeholder={T.emailPlaceholder}
                className="w-full px-3.5 py-2.5 rounded-xl border border-[#d2d2d7]/80 focus:border-[#161b3d] focus:ring-2 focus:ring-[#161b3d]/20 outline-none text-[14px] text-[#1d1d1f] bg-[#f5f5f7] focus:bg-white transition-all"
              />
            </div>

            {error && (
              <div className="rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-[13px] text-red-600">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl text-[14px] font-semibold text-white transition-opacity disabled:opacity-60"
              style={{ backgroundColor: BRAND_NAVY }}
            >
              {loading ? T.checking : T.enter}
            </button>
          </form>

          <p className="mt-5 text-center text-[12px] text-[#6e6e73]">
            {T.noAccountQuestion}{" "}
            <Link to="/" className="font-semibold text-[#8b1e22] hover:underline">
              {T.registerCta}
            </Link>
          </p>
        </div>

        <p className="mt-6 text-center text-[11px] text-[#86868b]">
          {T.footer}
        </p>
      </div>
    </motion.div>
  );
}
